/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Divider,
    useMultiStyleConfig,
    StylesProvider,
    Flex,
    Container
} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import FindStoreLink from '../find-store/find-store-link'

import LinksList from '../links-list'
import {HideOnDesktop} from '../responsive'
import {FooterSection, FooterSectionContainer} from '../footer-section'
import LanguageLink from './language-link'
import SpecialOffer from './partials/special-offer'
import PaymentIcons from './payment-icons'
import ShippingIcons from './shipping-icons'
import {FooterLinks} from './footer-links'
import SocialIcons from '../social-icons'
import CookieSettingsLink from './cookie-settings-link'
import useMultiSite from '../../hooks/use-multi-site'

const Footer = ({footerContent, ...otherProps}) => {
    const styles = useMultiStyleConfig('Footer')
    const intl = useIntl()
    const links = footerContent?.c_body?.innerJSON?.links || []
    const legalText = footerContent?.c_body?.innerJSON?.legalText || ''
    const legalLinks = footerContent?.c_body?.innerJSON?.legalLinks || []
    const copyrightText = footerContent?.c_body?.innerJSON?.copyrightText || []
    const socialIcons = footerContent?.c_body?.innerJSON?.socialIcons || []
    const specialOffers = footerContent?.c_body?.innerJSON?.specialOffers || []
    const {site} = useMultiSite()

    return (
        <Box as="footer" {...styles.container} {...otherProps}>
            <StylesProvider value={styles}>
                <Box backgroundColor="stokkeGray.mid">
                    <Container variant="footer" paddingTop={10} paddingBottom={12}>
                        <FooterSectionContainer>
                            {links?.map?.((list) => (
                                <FooterSection key={list.heading} label={list.heading}>
                                    <LinksList links={list.items} variant="footer" />
                                </FooterSection>
                            ))}
                            <FooterSection
                                label={intl.formatMessage({
                                    id: 'footer.promo.special_offers',
                                    defaultMessage: 'Special Offers'
                                })}
                                gridColumn={{
                                    md: 1,
                                    lg: 4
                                }}
                                gridRow={{
                                    md: '2 / 4',
                                    lg: '1 / 3'
                                }}
                            >
                                {specialOffers?.length > 0 && (
                                    <Flex flexDirection="column" alignItems="flex-start" gap={6}>
                                        {specialOffers.map((offer, idx) => (
                                            <SpecialOffer
                                                key={idx}
                                                {...offer}
                                                locationId={`footer_special_offers_${idx + 1}`}
                                            />
                                        ))}
                                    </Flex>
                                )}
                            </FooterSection>
                            {socialIcons && (
                                <FooterSection
                                    label={intl.formatMessage({
                                        id: 'footer.social.follow_us',
                                        defaultMessage: 'Say Hei and follow us on'
                                    })}
                                >
                                    <SocialIcons
                                        socialIcons={socialIcons}
                                        variant="flex-start"
                                        pinterestInnerColor="black"
                                    />
                                </FooterSection>
                            )}
                            <FooterSection
                                label={intl.formatMessage({
                                    defaultMessage: 'Find a store',
                                    id: 'footer.find_store.heading.label'
                                })}
                            >
                                <FindStoreLink />
                            </FooterSection>
                        </FooterSectionContainer>
                    </Container>
                </Box>

                <Box backgroundColor="stokkeGray.bright">
                    <Container variant="footer" paddingTop={6} paddingBottom={6}>
                        {!site?.isContentOnlySite && (
                            <Flex
                                flexDirection={{base: 'column', lg: 'row'}}
                                alignItems={{base: 'center', lg: 'flex-start'}}
                                justifyContent="center"
                                gap={{base: 8, lg: 16}}
                            >
                                <PaymentIcons variant="center" maxWidth="40rem" />
                                <HideOnDesktop>
                                    <ShippingIcons variant="center" maxWidth="40rem" />
                                </HideOnDesktop>
                            </Flex>
                        )}
                        <Box {...styles.horizontalRule.container}>
                            <Divider {...styles.horizontalRule.divider} />
                        </Box>

                        <Flex
                            flexDirection={{base: 'column', md: 'row-reverse'}}
                            gap={{base: 6, md: 4}}
                            alignItems={{base: 'flex-start', md: 'center'}}
                            justifyContent="center"
                        >
                            <LanguageLink />
                            <FooterLinks variant="footerLinks" links={legalLinks}>
                                <CookieSettingsLink />
                            </FooterLinks>
                        </Flex>

                        <Box {...styles.horizontalRule.container}>
                            <Divider {...styles.horizontalRule.divider} />
                        </Box>

                        {copyrightText && legalText && (
                            <Box {...styles.bottomHalf} textStyle="Regular Extra Small">
                                <span>©</span>
                                <Text as="span" textStyle="Semi Bold Extra Small">
                                    {copyrightText}
                                </Text>
                                <Text as="span">{legalText}</Text>
                            </Box>
                        )}
                    </Container>
                </Box>
            </StylesProvider>
        </Box>
    )
}

Footer.propTypes = {
    footerContent: PropTypes.object
}

export default Footer
